import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiCheckCircleFill,
    PiDownloadSimple,
    PiDownloadSimpleBold,
    PiFileCsvBold,
    PiFileXlsBold,
    PiWarningCircleFill,
} from 'react-icons/pi';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { pop } from '@v2/lib/motion';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';

type SipoFormat = 'xlsx' | 'csv_comma' | 'csv_semicolon';

type ExportState = {
    token: string;
    status: 'queued' | 'processing' | 'ready' | 'failed' | 'expired';
    format: SipoFormat | null;
    filename: string | null;
    line_count: number | null;
    total_usd: number | null;
    error: string | null;
    download_url: string | null;
};

const FORMAT_STORAGE_KEY = 'v2.sipo_export.format';

const FORMATS: { value: SipoFormat; icon: typeof PiFileXlsBold; ext: string }[] = [
    { value: 'xlsx', icon: PiFileXlsBold, ext: '.xlsx' },
    { value: 'csv_semicolon', icon: PiFileCsvBold, ext: '.csv ;' },
    { value: 'csv_comma', icon: PiFileCsvBold, ext: '.csv ,' },
];

function readStoredFormat(): SipoFormat {
    try {
        const v = window.localStorage.getItem(FORMAT_STORAGE_KEY);
        if (v === 'xlsx' || v === 'csv_comma' || v === 'csv_semicolon') return v;
    } catch {
        /* sem storage: usa o padrão */
    }
    return 'xlsx';
}

const usd = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function ProtheusSipoExportButton({ shipmentId }: { shipmentId: number }) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const [format, setFormat] = useState<SipoFormat>(readStoredFormat);
    const [token, setToken] = useState<string | null>(null);
    const successRef = useRef<HTMLDivElement>(null);

    const startMut = useMutation({
        mutationFn: async (f: SipoFormat) => {
            const { data } = await api.post<{ data: ExportState }>(`/shipments/${shipmentId}/protheus-sipo/exports`, {
                format: f,
            });
            return data.data;
        },
        onSuccess: (state) => setToken(state.token),
        onError: () => toast.error(t('v2.sipo_export.start_error')),
    });

    const statusQuery = useQuery({
        queryKey: ['protheus-sipo-export', token],
        enabled: token !== null,
        queryFn: async () => {
            const { data } = await api.get<{ data: ExportState }>(`/protheus-sipo/exports/${token}`);
            return data.data;
        },
        refetchInterval: (query) => {
            const s = query.state.data?.status;
            return s === 'ready' || s === 'failed' || s === 'expired' ? false : 1500;
        },
    });

    const state = statusQuery.data ?? null;
    const status = token === null ? 'idle' : (state?.status ?? 'queued');
    const running = status === 'queued' || status === 'processing';

    // Fechou o modal enquanto gerava: quando fica pronto o modal volta sozinho.
    useEffect(() => {
        if (status === 'ready' || status === 'failed') {
            setOpen(true);
            if (status === 'ready') {
                window.setTimeout(() => pop(successRef.current), 30);
            }
        }
    }, [status]);

    const generate = () => {
        try {
            window.localStorage.setItem(FORMAT_STORAGE_KEY, format);
        } catch {
            /* preferência é só conveniência */
        }
        setToken(null);
        startMut.mutate(format);
    };

    const reset = () => setToken(null);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title={t('v2.sipo_export.button')}
                className={cn('relative rounded p-1 text-indigo-700 hover:bg-indigo-50', running && 'animate-pulse')}
            >
                <PiDownloadSimple className="h-4 w-4" />
                {running && <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-indigo-500" />}
            </button>

            <V2Modal
                open={open}
                onClose={() => setOpen(false)}
                title={t('v2.sipo_export.title')}
                description={t('v2.sipo_export.description')}
                size="md"
                footer={
                    status === 'ready' && state?.download_url ? (
                        <>
                            <Button variant="outline" onClick={reset}>
                                {t('v2.sipo_export.another_format')}
                            </Button>
                            <a href={state.download_url}>
                                <Button>
                                    <PiDownloadSimpleBold className="me-1.5 size-4" />
                                    {t('v2.sipo_export.download')}
                                </Button>
                            </a>
                        </>
                    ) : running ? (
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            {t('v2.sipo_export.keep_working')}
                        </Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setOpen(false)}>
                                {t('common.close')}
                            </Button>
                            <Button onClick={generate} isLoading={startMut.isPending}>
                                {status === 'failed' || status === 'expired' ? t('v2.sipo_export.retry') : t('v2.sipo_export.generate')}
                            </Button>
                        </>
                    )
                }
            >
                {status === 'idle' || status === 'failed' || status === 'expired' ? (
                    <div className="space-y-3">
                        {status !== 'idle' && (
                            <p className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
                                <PiWarningCircleFill className="mt-0.5 size-4 shrink-0" />
                                {status === 'expired' ? t('v2.sipo_export.expired') : (state?.error ?? t('v2.sipo_export.failed'))}
                            </p>
                        )}
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{t('v2.sipo_export.choose_format')}</p>
                        <div className="grid gap-2" role="radiogroup">
                            {FORMATS.map((f) => {
                                const active = format === f.value;
                                const Icon = f.icon;
                                return (
                                    <button
                                        key={f.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        onClick={() => setFormat(f.value)}
                                        className={cn(
                                            'flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition',
                                            active
                                                ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                                : 'border-gray-200 hover:border-gray-300 dark:border-zinc-700',
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                'flex size-9 shrink-0 items-center justify-center rounded-lg',
                                                f.value === 'xlsx'
                                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                                                    : 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
                                            )}
                                        >
                                            <Icon className="size-5" />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm font-semibold text-gray-900 dark:text-zinc-100">
                                                {t(`v2.sipo_export.format_${f.value}`)}
                                            </span>
                                            <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                                                {t(`v2.sipo_export.format_${f.value}_hint`)}
                                            </span>
                                        </span>
                                        <span className="font-mono text-[11px] text-gray-400">{f.ext}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : running ? (
                    <div className="flex flex-col items-center gap-3 py-6 text-center">
                        <span className="relative flex size-14 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300">
                            <span className="absolute inset-0 animate-ping rounded-full bg-indigo-200/60 motion-reduce:animate-none dark:bg-indigo-500/20" />
                            <PiFileXlsBold className="relative size-7" />
                        </span>
                        <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                            {status === 'queued' ? t('v2.sipo_export.queued') : t('v2.sipo_export.processing')}
                        </p>
                        <p className="max-w-xs text-xs text-gray-500 dark:text-gray-400">{t('v2.sipo_export.background_hint')}</p>
                        <div className="h-1.5 w-48 overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
                            <div className="h-full w-1/3 animate-[sipo-bar_1.2s_ease-in-out_infinite] rounded-full bg-indigo-500 motion-reduce:animate-none" />
                        </div>
                        <style>{'@keyframes sipo-bar{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}'}</style>
                    </div>
                ) : (
                    <div ref={successRef} className="flex flex-col items-center gap-2 py-5 text-center">
                        <PiCheckCircleFill className="size-14 text-emerald-500" />
                        <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{t('v2.sipo_export.ready')}</p>
                        <p className="break-all font-mono text-xs text-gray-600 dark:text-gray-300">{state?.filename}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('v2.sipo_export.summary', {
                                lines: state?.line_count ?? 0,
                                total: usd(state?.total_usd ?? 0),
                            })}
                        </p>
                    </div>
                )}
            </V2Modal>
        </>
    );
}
